export type QueryResult = {
  rows?: {
    _array?: Record<string, unknown>[]
    length?: number
    item?: (idx: number) => Record<string, unknown> | undefined
  }
  rowsAffected?: number
  insertId?: number
  results?: Record<string, unknown>[]
}

export type NitroSQLiteConnection = {
  close: () => void
  delete: () => void
  execute: (sql: string, params?: unknown[]) => QueryResult
  executeAsync: (sql: string, params?: unknown[]) => Promise<QueryResult>
  transaction: <T>(cb: (tx: NitroSQLiteConnection) => Promise<T>) => Promise<T>
}

type MemTable = {
  columns: string[]
  rows: Record<string, unknown>[]
  nextId: number
}

function parseInsertColumns(sql: string): string[] | null {
  const match = sql.match(/INSERT\s+INTO\s+\w+\s*\(([^)]+)\)/i)
  if (!match) return null
  return match[1].split(",").map((c) => c.trim())
}

function rowMatchesWhere(row: Record<string, unknown>, sql: string, params: unknown[]): boolean {
  const upper = sql.toUpperCase()
  if (!upper.includes(" WHERE ")) return true

  const wherePart = sql.match(/\bWHERE\b([\s\S]*?)(?:\bORDER\b|\bLIMIT\b|\bOFFSET\b|;|$)/i)?.[1] ?? ""
  let paramIdx = 0
  const tests = wherePart.split(/\bAND\b/i)

  for (const test of tests) {
    const trimmed = test.trim()
    const eq = trimmed.match(/^(\w+)\s*=\s*\?$/i)
    if (eq) {
      const col = eq[1]
      const expected = params[paramIdx++]
      if (String(row[col]) !== String(expected)) return false
      continue
    }
    const inMatch = trimmed.match(/^(\w+)\s+IN\s*\(([^)]+)\)$/i)
    if (inMatch) {
      const col = inMatch[1]
      const placeholders = inMatch[2].split(",").map((s) => s.trim())
      const values = placeholders.map(() => params[paramIdx++])
      if (!values.some((v) => String(row[col]) === String(v))) return false
    }
  }
  return true
}

function sortRows(rows: Record<string, unknown>[], sql: string): Record<string, unknown>[] {
  const orderMatch = sql.match(/\bORDER\s+BY\s+(\w+)(?:\s+(ASC|DESC))?(?:\s*,\s*(\w+)(?:\s+(ASC|DESC))?)?/i)
  if (!orderMatch) return rows

  const [, col1, dir1 = "ASC", col2, dir2 = "ASC"] = orderMatch
  const mul1 = dir1.toUpperCase() === "DESC" ? -1 : 1
  const mul2 = dir2.toUpperCase() === "DESC" ? -1 : 1

  return [...rows].sort((a, b) => {
    const v1 = Number(a[col1]) - Number(b[col1])
    if (v1 !== 0) return v1 * mul1
    if (col2) return (Number(a[col2]) - Number(b[col2])) * mul2
    return 0
  })
}

function limitRows(rows: Record<string, unknown>[], sql: string): Record<string, unknown>[] {
  const limitMatch = sql.match(/\bLIMIT\s+(\d+)/i)
  const offsetMatch = sql.match(/\bOFFSET\s+(\d+)/i)
  const limit = limitMatch ? Number(limitMatch[1]) : rows.length
  const offset = offsetMatch ? Number(offsetMatch[1]) : 0
  return rows.slice(offset, offset + limit)
}

class MemDatabase {
  tables = new Map<string, MemTable>()

  exec(sql: string, params: unknown[] = []): QueryResult {
    const normalized = sql.trim().replace(/;+\s*$/, "")
    const upper = normalized.toUpperCase()

    if (upper.startsWith("CREATE TABLE")) {
      const name = normalized.match(/CREATE TABLE IF NOT EXISTS\s+(\w+)/i)?.[1]
      if (name && !this.tables.has(name)) {
        const body = normalized.match(/\(([\s\S]+)\)/)?.[1] ?? ""
        const cols =
          body
            .split(",")
            .map((c) => c.trim().split(/\s+/)[0])
            .filter((c) => c && !c.startsWith("PRIMARY") && !c.startsWith("UNIQUE")) || []
        this.tables.set(name, { columns: cols, rows: [], nextId: 1 })
      }
      return { rowsAffected: 0, results: [] }
    }

    if (upper.startsWith("DROP TABLE")) {
      const name = normalized.match(/DROP TABLE IF EXISTS\s+(\w+)/i)?.[1]
      if (name) this.tables.delete(name)
      return { rowsAffected: 0, results: [] }
    }

    if (upper.startsWith("PRAGMA TABLE_INFO")) {
      const name = normalized.match(/PRAGMA\s+table_info\((\w+)\)/i)?.[1]
      const table = name ? this.tables.get(name) : undefined
      const results = table?.columns.map((col) => ({ name: col, type: "TEXT" })) ?? []
      return { rowsAffected: 0, results }
    }

    if (upper.startsWith("INSERT INTO")) {
      const name = normalized.match(/INSERT INTO\s+(\w+)/i)?.[1]
      const table = name ? this.tables.get(name) : undefined
      if (!table) return { rowsAffected: 0, results: [] }

      const insertCols = parseInsertColumns(normalized)
      const row: Record<string, unknown> = {}

      if (insertCols?.length) {
        insertCols.forEach((col, idx) => {
          if (col === "id" && (params[idx] === null || params[idx] === undefined)) return
          row[col] = params[idx]
        })
        if (row.id == null) row.id = table.nextId++
      } else {
        row.id = table.nextId++
        table.columns.forEach((col, idx) => {
          if (col !== "id") row[col] = params[idx]
        })
      }

      if (row.id == null) row.id = table.nextId++
      table.rows.push(row)
      return { rowsAffected: 1, insertId: row.id as number, results: [row] }
    }

    if (upper.startsWith("UPDATE")) {
      const name = normalized.match(/UPDATE\s+(\w+)/i)?.[1]
      const table = name ? this.tables.get(name) : undefined
      if (!table) return { rowsAffected: 0, results: [] }

      const setMatch = normalized.match(/\bSET\b([\s\S]*?)\bWHERE\b/i)
      const setPart = setMatch?.[1] ?? ""
      const setCols = setPart.split(",").map((s) => s.trim().split(/\s*=\s*\?/)[0].trim())
      const setValues = params.slice(0, setCols.length)
      const whereParams = params.slice(setCols.length)

      let affected = 0
      table.rows = table.rows.map((row) => {
        if (!rowMatchesWhere(row, normalized, whereParams)) return row
        const next = { ...row }
        setCols.forEach((col, idx) => {
          next[col] = setValues[idx]
        })
        affected++
        return next
      })
      return { rowsAffected: affected, results: [] }
    }

    if (upper.startsWith("SELECT")) {
      const name = normalized.match(/FROM\s+(\w+)/i)?.[1]
      const table = name ? this.tables.get(name) : undefined
      let results = (table?.rows ?? []).filter((row) => rowMatchesWhere(row, normalized, params))
      results = sortRows(results, normalized)
      results = limitRows(results, normalized)
      return {
        results,
        rows: {
          _array: results,
          length: results.length,
          item: (idx: number) => results[idx],
        },
      }
    }

    if (upper.startsWith("DELETE FROM")) {
      const name = normalized.match(/DELETE FROM\s+(\w+)/i)?.[1]
      const table = name ? this.tables.get(name) : undefined
      if (!table) return { rowsAffected: 0, results: [] }

      if (!upper.includes(" WHERE ")) {
        const before = table.rows.length
        table.rows = []
        return { rowsAffected: before, results: [] }
      }

      const before = table.rows.length
      table.rows = table.rows.filter((row) => !rowMatchesWhere(row, normalized, params))
      return { rowsAffected: before - table.rows.length, results: [] }
    }

    return { rowsAffected: 0, results: [] }
  }
}

const databases = new Map<string, MemDatabase>()

function getDb(name: string) {
  if (!databases.has(name)) databases.set(name, new MemDatabase())
  return databases.get(name)!
}

export function open(options: { name: string }): NitroSQLiteConnection {
  const db = getDb(options.name)
  const conn: NitroSQLiteConnection = {
    close: () => {},
    delete: () => databases.delete(options.name),
    execute: (sql, params = []) => db.exec(sql, params),
    executeAsync: async (sql, params = []) => db.exec(sql, params),
    transaction: async (cb) => cb(conn),
  }
  return conn
}
